// src/pages/ServiceDetail.jsx
// Modified to use server-side OG generation
// Social media scrapers get OG tags from API, not from client-side Helmet

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FiArrowLeft,
  FiCalendar,
  FiTag,
  FiUser,
  FiHeart,
  FiEye,
  FiShare2,
  FiClock,
  FiDollarSign,
  FiCheckCircle,
  FiPackage,
  FiUsers,
  FiStar,
  FiArrowRight,
  FiGrid,
  FiTrendingUp,
  FiZap,
  FiAward,
  FiFolder,
  FiExternalLink,
  FiMessageCircle,
  FiInfo,
  FiSend,
  FiAlertCircle,
  FiChevronDown,
  FiChevronUp,
  FiHelpCircle,
  FiMail,
  FiPhone,
  FiAlertTriangle,
  FiCheck,
  FiX,
  FiCopy,
  FiTwitter,
  FiFacebook,
  FiLinkedin,
  FiInstagram,
  FiLink,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import {
  db,
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from '../services/firebase';
import ServiceDetailSkeleton from '../components/services/ServiceDetailSkeleton';
import { EmailValidationInput } from '../utils/emailValidation.jsx';
import { readyMadeService } from '../services/readyMadeService';
import { useTheme } from '../contexts/ThemeContext';
import { FiverrCard } from '../components/FiverrCard';
// ✅ Import simplified ogImageService for share modal ONLY
import { ogImageService } from '../services/ogImageService';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const DEFAULT_IMAGE = '/og-images/default/og-image-default.png';

// =============================================
// SOCIAL SHARE MODAL - Uses simplified ogImageService
// =============================================
const SocialShareModal = ({ isOpen, onClose, service, shareUrl }) => {
  const [copied, setCopied] = useState(false);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackShare('copy');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const trackShare = async platform => {
    try {
      console.log(`Shared on ${platform}: ${shareUrl}`);
      if (window.gtag) {
        window.gtag('event', 'share', {
          method: platform,
          content_type: 'service',
          content_id: service?.id,
          content_title: service?.name,
        });
      }
    } catch (error) {
      console.error('Share tracking failed:', error);
    }
  };

  // ✅ Use simplified ogImageService for modal preview ONLY
  const shareData = useMemo(() => {
    if (!service) return null;
    return ogImageService.service(service);
  }, [service]);

  if (!isOpen || !service || !shareData) return null;

  const sharePlatforms = [
    {
      id: 'twitter',
      name: 'Twitter',
      icon: FiTwitter,
      color: '#1DA1F2',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(service.name)}&url=${encodeURIComponent(shareUrl)}&via=wdhejbul`,
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: FiFacebook,
      color: '#1877F2',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(service.name)}`,
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: FiLinkedin,
      color: '#0A66C2',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      icon: FaWhatsapp,
      color: '#25D366',
      url: `https://api.whatsapp.com/send?text=${encodeURIComponent(`📝 ${service.name}\n\n${shareUrl}`)}`,
    },
    {
      id: 'telegram',
      name: 'Telegram',
      icon: FiSend,
      color: '#26A5E4',
      url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`📝 ${service.name}`)}`,
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: FiInstagram,
      color: '#E4405F',
      url: null,
      action: () => {
        copyToClipboard();
        alert('📸 Instagram: Link copied! Open Instagram app to share.');
        trackShare('instagram');
      },
    },
    {
      id: 'email',
      name: 'Email',
      icon: FiMail,
      color: '#EA4335',
      url: `mailto:?subject=${encodeURIComponent(`Check out: ${service.name}`)}&body=${encodeURIComponent(`I thought you might find this interesting:\n\n${service.name}\n${shareUrl}`)}`,
    },
  ];

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      style={{ animation: 'fadeIn 0.2s ease-out' }}
    >
      <div
        className="relative max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
        style={{
          backgroundColor: isDark
            ? 'rgba(20,20,20,0.98)'
            : 'rgba(255,255,255,0.98)',
          backdropFilter: 'blur(20px)',
          border: isDark
            ? '1px solid rgba(255,255,255,0.08)'
            : '1px solid rgba(0,0,0,0.06)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="sticky top-0 px-6 py-4 border-b flex items-center justify-between"
          style={{
            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
            backgroundColor: isDark
              ? 'rgba(20,20,20,0.95)'
              : 'rgba(255,255,255,0.95)',
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                backgroundColor: isDark
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.05)',
              }}
            >
              <FiShare2 className="w-5 h-5" style={{ color: 'var(--text)' }} />
            </div>
            <div>
              <h3
                className="text-lg font-semibold"
                style={{ color: 'var(--text)' }}
              >
                Share this service
              </h3>
              <p className="text-xs" style={{ color: 'var(--muted)' }}>
                Spread the word about "{service.name}"
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5"
          >
            <FiX className="w-5 h-5" style={{ color: 'var(--text)' }} />
          </button>
        </div>

        {/* Preview Card - Uses simplified shareData */}
        <div className="px-6 pt-4">
          <div
            className="rounded-xl overflow-hidden border"
            style={{
              borderColor: isDark
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(0,0,0,0.06)',
            }}
          >
            <div className="relative aspect-[1.91/1] bg-gray-100 dark:bg-gray-800">
              <img
                src={shareData.image || DEFAULT_IMAGE}
                alt={service.name}
                className="w-full h-full object-cover"
                onError={e => {
                  e.target.src = DEFAULT_IMAGE;
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 via-black/50 to-transparent">
                <p className="text-white font-semibold text-lg line-clamp-1">
                  {shareData.title || service.name}
                </p>
                <p className="text-white/70 text-sm line-clamp-1">
                  {shareData.description || service.description}
                </p>
                <p className="text-white/40 text-xs mt-0.5">{shareUrl}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Share Buttons Grid */}
        <div className="px-6 py-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {sharePlatforms.map(platform => {
              const Icon = platform.icon;
              const isDisabled = !platform.url && !platform.action;

              return (
                <button
                  key={platform.id}
                  onClick={() => {
                    if (platform.action) {
                      platform.action();
                    } else if (platform.url) {
                      window.open(
                        platform.url,
                        '_blank',
                        'noopener,noreferrer,width=600,height=400',
                      );
                      trackShare(platform.id);
                    }
                  }}
                  disabled={isDisabled}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all duration-200 ${
                    isDisabled
                      ? 'opacity-40 cursor-not-allowed'
                      : 'hover:scale-105 hover:shadow-md active:scale-95'
                  }`}
                  style={{
                    backgroundColor: isDark
                      ? 'rgba(255,255,255,0.03)'
                      : 'rgba(0,0,0,0.02)',
                    border: isDark
                      ? '1px solid rgba(255,255,255,0.06)'
                      : '1px solid rgba(0,0,0,0.06)',
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{
                      backgroundColor: `${platform.color}15`,
                      color: platform.color,
                    }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <span
                    className="text-xs font-medium"
                    style={{ color: 'var(--text)' }}
                  >
                    {platform.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Copy Link Section */}
        <div
          className="px-6 pb-6 border-t"
          style={{
            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
          }}
        >
          <div className="pt-4">
            <div
              className="flex items-center gap-2 p-2 rounded-xl border"
              style={{ borderColor: 'var(--border)' }}
            >
              <FiLink
                className="w-4 h-4 flex-shrink-0"
                style={{ color: 'var(--muted)' }}
              />
              <p
                className="text-xs truncate flex-1"
                style={{ color: 'var(--muted)' }}
              >
                {shareUrl}
              </p>
              <button
                onClick={copyToClipboard}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 flex items-center gap-1.5 ${
                  copied
                    ? 'text-green-500 bg-green-500/10'
                    : 'hover:bg-black/5 dark:hover:bg-white/5'
                }`}
                style={{ color: copied ? '#22c55e' : 'var(--text)' }}
              >
                {copied ? (
                  <>
                    <FiCheckCircle className="w-3.5 h-3.5" />
                    Copied!
                  </>
                ) : (
                  <>
                    <FiCopy className="w-3.5 h-3.5" />
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// =============================================
// SHARE BUTTONS - Updated with Modal
// =============================================
const ShareButtons = ({ url, title, description, imageUrl, service }) => {
  const [copied, setCopied] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackShare('copy');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const trackShare = async platform => {
    try {
      console.log(`Shared on ${platform}: ${url}`);
      if (window.gtag) {
        window.gtag('event', 'share', {
          method: platform,
          content_type: 'service',
          content_id: service?.id,
          content_title: title,
        });
      }
    } catch (error) {
      console.error('Share tracking failed:', error);
    }
  };

  const quickShare = platform => {
    const encodedTitle = encodeURIComponent(`📝 ${title}`);
    const encodedUrl = encodeURIComponent(url);

    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}&via=wdhejbul`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      whatsapp: `https://api.whatsapp.com/send?text=${encodedTitle}%0A${encodedUrl}`,
      telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
      instagram: null,
      email: `mailto:?subject=${encodedTitle}&body=${encodedTitle}%0A%0A${encodedUrl}`,
    };

    if (platform === 'instagram') {
      copyToClipboard();
      alert('📸 Instagram: Link copied! Open Instagram app to share.');
      trackShare('instagram');
      return;
    }

    const shareUrl = urls[platform];
    if (shareUrl) {
      window.open(
        shareUrl,
        '_blank',
        'noopener,noreferrer,width=600,height=400',
      );
      trackShare(platform);
    }
  };

  const quickPlatforms = [
    { id: 'twitter', icon: FiTwitter, color: '#1DA1F2' },
    { id: 'facebook', icon: FiFacebook, color: '#1877F2' },
    { id: 'linkedin', icon: FiLinkedin, color: '#0A66C2' },
    { id: 'whatsapp', icon: FaWhatsapp, color: '#25D366' },
    { id: 'telegram', icon: FiSend, color: '#26A5E4' },
  ];

  return (
    <>
      <div className="flex items-center gap-1.5">
        <button
          onClick={copyToClipboard}
          className={`p-1.5 rounded-lg transition-all duration-200 cursor-pointer ${
            copied
              ? 'text-green-500 bg-green-500/10'
              : 'hover:bg-black/5 dark:hover:bg-white/5'
          }`}
          style={{ color: copied ? '#22c55e' : 'var(--text)' }}
          aria-label="Copy link"
          title="Copy link"
        >
          {copied ? (
            <FiCheckCircle className="w-4 h-4" />
          ) : (
            <FiCopy className="w-4 h-4" />
          )}
        </button>

        {quickPlatforms.map(platform => {
          const Icon = platform.icon;
          return (
            <button
              key={platform.id}
              onClick={() => quickShare(platform.id)}
              className="p-1.5 rounded-lg transition-all duration-200 cursor-pointer hover:scale-110 hover:bg-black/5 dark:hover:bg-white/5"
              style={{ color: 'var(--text)' }}
              aria-label={`Share on ${platform.id}`}
              title={`Share on ${platform.id}`}
            >
              <Icon className="w-4 h-4" />
            </button>
          );
        })}

        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer text-sm"
          style={{ color: 'var(--text)' }}
          aria-label="More share options"
          title="More share options"
        >
          <FiShare2 className="w-4 h-4" />
          <span className="hidden sm:inline">Share</span>
        </button>
      </div>

      {showModal && (
        <SocialShareModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          service={service}
          shareUrl={url}
        />
      )}
    </>
  );
};

// =============================================
// WORKFLOW TOOLTIP - Compact Version
// =============================================
const WorkflowTooltip = () => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="relative inline-block" ref={tooltipRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className="flex items-center gap-1 text-[10px] transition-colors hover:opacity-70"
        style={{ color: 'var(--muted)' }}
      >
        <FiHelpCircle className="w-3 h-3" />
        <span>How it works</span>
        <FiChevronDown
          className={`w-2 h-2 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          className={`absolute bottom-full mb-2 w-72 p-3 rounded-lg shadow-xl z-50 animate-fade-in border ${
            isMobile ? 'right-0 left-auto' : 'left-0'
          }`}
          style={{
            backgroundColor: 'var(--bg)',
            borderColor: 'var(--border)',
            color: 'var(--text)',
            maxWidth: 'calc(100vw - 32px)',
          }}
        >
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5">
                1
              </div>
              <div>
                <p className="text-xs font-medium">Submit your inquiry</p>
                <p className="text-[10px]" style={{ color: 'var(--muted)' }}>
                  Fill out the form with your project details
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5">
                2
              </div>
              <div>
                <p className="text-xs font-medium">Get a response</p>
                <p className="text-[10px]" style={{ color: 'var(--muted)' }}>
                  We'll respond within 24 hours
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5">
                3
              </div>
              <div>
                <p className="text-xs font-medium">Collaborate manually</p>
                <p className="text-[10px]" style={{ color: 'var(--muted)' }}>
                  We'll work together to complete your project
                </p>
              </div>
            </div>
            <div
              className="pt-1.5 mt-1 border-t"
              style={{ borderColor: 'var(--border)' }}
            >
              <p
                className="text-[10px] italic"
                style={{ color: 'var(--muted)' }}
              >
                💡 No automated ordering. We collaborate manually through direct
                communication.
              </p>
            </div>
          </div>
          <div
            className={`absolute top-full w-2.5 h-2.5 rotate-45 -mt-1.5 ${
              isMobile ? 'right-6 left-auto' : 'left-6'
            }`}
            style={{
              backgroundColor: 'var(--bg)',
              borderRight: '1px solid var(--border)',
              borderBottom: '1px solid var(--border)',
            }}
          />
        </div>
      )}
    </div>
  );
};

// =============================================
// INQUIRY FORM - Compact Version
// =============================================
const InquiryForm = ({ service, onSuccess }) => {
  const { actualTheme } = useTheme();
  const isDark = actualTheme === 'dark';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [showEmailTooltip, setShowEmailTooltip] = useState(false);
  const [showSpamAlert, setShowSpamAlert] = useState(true);

  const [emailValidation, setEmailValidation] = useState({
    isValid: false,
    errorMessage: null,
    suggestion: null,
    qualityScore: 0,
    isValidating: false,
  });

  const [touchedFields, setTouchedFields] = useState({
    name: false,
    email: false,
    phone: false,
    message: false,
  });

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errorMessage) setErrorMessage('');
    if (submitStatus === 'error') setSubmitStatus(null);
  };

  const handleBlur = fieldName => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
  };

  const handleEmailValidationChange = (isValid, validationResult) => {
    setEmailValidation({
      isValid: isValid,
      errorMessage: validationResult?.reason || null,
      suggestion: validationResult?.suggestions || null,
      qualityScore: validationResult?.score || 0,
      isValidating: false,
    });

    if (isValid && errorMessage && errorMessage.includes('email')) {
      setErrorMessage('');
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setErrorMessage('Please enter your name');
      return false;
    }

    if (!emailValidation.isValid) {
      setErrorMessage(
        emailValidation.errorMessage || 'Please enter a valid email address',
      );
      return false;
    }

    if (!formData.message.trim()) {
      setErrorMessage('Please tell us about your project or requirements');
      return false;
    }

    if (formData.message.trim().length < 20) {
      setErrorMessage('Please provide more details (minimum 20 characters)');
      return false;
    }

    return true;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    setTouchedFields({
      name: true,
      email: true,
      phone: true,
      message: true,
    });

    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const packageData = {
        id: service.id,
        name: service.name,
        price: service.price,
        duration: service.duration,
        features: service.features || [],
        gradient: service.gradient || 'from-purple-500 to-pink-500',
        imageUrl: service.imageUrl || null,
        category: service.category || 'others',
        popular: service.popular || false,
      };

      const inquiryData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        company: formData.company.trim() || '',
        phone: formData.phone.trim() || '',
        message: formData.message.trim(),
      };

      const result = await readyMadeService.submitInquiry(
        inquiryData,
        packageData,
      );

      console.log('✅ Inquiry submitted successfully:', result);
      setSubmitStatus('success');

      if (onSuccess) onSuccess();

      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: '',
        });
        setSubmitStatus(null);
      }, 5000);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      setSubmitStatus('error');
      setErrorMessage(
        error.message || 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid =
    formData.name.trim() &&
    emailValidation.isValid &&
    formData.message.trim() &&
    formData.message.trim().length >= 20;

  return (
    <div className="space-y-3">
      {/* Spam Alert Banner */}
      {showSpamAlert && (
        <div
          className="p-2.5 rounded-lg border animate-fade-in"
          style={{
            backgroundColor: 'rgba(245, 158, 11, 0.06)',
            borderColor: 'rgba(245, 158, 11, 0.2)',
          }}
        >
          <div className="flex items-start gap-2">
            <FiAlertTriangle className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p
                className="text-[11px] font-medium"
                style={{ color: '#f59e0b' }}
              >
                📬 Check Your Spam Folder
              </p>
              <p
                className="text-[10px] leading-relaxed"
                style={{ color: 'var(--muted)' }}
              >
                Our response may go to spam. Check there if you don't see our
                reply.
              </p>
              <div className="flex items-start gap-1.5 mt-1 p-1.5 rounded bg-blue-500/5 border border-blue-500/10">
                <FiMail className="w-3 h-3 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px]" style={{ color: 'var(--muted)' }}>
                    <span className="font-medium text-blue-500">Pro tip:</span>{' '}
                    Add{' '}
                    <span className="text-blue-500 font-mono text-[9px]">
                      wdhejbul@gmail.com
                    </span>{' '}
                    to your contacts — this tells your email provider
                    <span className="text-[9px] block mt-0.5 text-blue-400/70">
                      (Gmail, Outlook, etc.) that we're a trusted sender, so our
                      replies go straight to your inbox, not spam.
                    </span>
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowSpamAlert(false)}
                className="text-[9px] mt-1 hover:underline opacity-60"
                style={{ color: 'var(--muted)' }}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-2.5">
        <div>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onFocus={() => setFocusedField('name')}
            onBlur={() => {
              setFocusedField(null);
              handleBlur('name');
            }}
            placeholder="Full name *"
            className="w-full px-3 py-1.5 rounded-lg outline-none transition-all text-[13px]"
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${touchedFields.name && !formData.name ? '#ef4444' : 'var(--border)'}`,
              color: 'var(--text)',
              height: '36px',
            }}
          />
          <div className="h-3 mt-0.5">
            {touchedFields.name && !formData.name && (
              <div className="text-[9px] text-red-500">Name is required</div>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-1.5 mb-0.5">
            <label
              className="text-[10px] font-medium"
              style={{ color: 'var(--text)' }}
            >
              Email <span className="text-red-500">*</span>
            </label>
            <div
              className="relative"
              onMouseEnter={() => setShowEmailTooltip(true)}
              onMouseLeave={() => setShowEmailTooltip(false)}
            >
              <FiInfo
                className="w-3 h-3 cursor-help"
                style={{ color: '#3b82f6' }}
              />
              {showEmailTooltip && (
                <div
                  className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 p-2 rounded-lg shadow-lg z-50 animate-fade-in"
                  style={{
                    backgroundColor: 'var(--bg)',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <div className="text-[9px] space-y-0.5">
                    <p className="font-semibold text-[10px]">
                      Why we need your email?
                    </p>
                    <p>• Get confirmation of your inquiry</p>
                    <p>• Receive updates about your request</p>
                    <p>• Get our response within 24 hours</p>
                    <p className="text-yellow-500 mt-1 text-[9px]">
                      ⚠️ Check spam if you don't see our reply!
                    </p>
                  </div>
                  <div
                    className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 rotate-45"
                    style={{
                      backgroundColor: 'var(--bg)',
                      borderRight: '1px solid var(--border)',
                      borderBottom: '1px solid var(--border)',
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <EmailValidationInput
            value={formData.email}
            onChange={e => {
              setFormData(prev => ({ ...prev, email: e.target.value }));
              if (errorMessage && errorMessage.includes('email'))
                setErrorMessage('');
            }}
            onBlur={() => handleBlur('email')}
            placeholder="your@email.com"
            className="w-full"
            inputClassName="w-full px-3 py-1.5 rounded-lg outline-none transition-all text-[13px] h-[36px]"
            onValidationChange={handleEmailValidationChange}
          />
          <div className="h-3 mt-0.5">
            {touchedFields.email && !emailValidation.isValid && (
              <div className="text-[9px] text-red-500">
                {emailValidation.errorMessage ||
                  'Please enter a valid email address'}
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-1.5 mb-0.5">
            <label
              className="text-[10px] font-medium"
              style={{ color: 'var(--text)' }}
            >
              Phone{' '}
              <span className="text-yellow-500 text-[9px]">(recommended)</span>
            </label>
            <FiInfo
              className="w-3 h-3 cursor-help"
              style={{ color: 'var(--muted)' }}
            />
          </div>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            onFocus={() => setFocusedField('phone')}
            onBlur={() => {
              setFocusedField(null);
              handleBlur('phone');
            }}
            placeholder="+880 1884 521 488 (backup contact)"
            className="w-full px-3 py-1.5 rounded-lg outline-none transition-all text-[13px]"
            style={{
              backgroundColor: 'transparent',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              height: '36px',
            }}
          />
          <div className="h-3 mt-0.5">
            <p className="text-[9px]" style={{ color: 'var(--muted)' }}>
              <FiAlertTriangle className="inline w-2.5 h-2.5 text-yellow-500 mr-0.5" />
              Helps us contact you directly if our email goes to spam.
            </p>
          </div>
        </div>

        <div>
          <input
            type="text"
            name="company"
            value={formData.company}
            onChange={handleChange}
            onFocus={() => setFocusedField('company')}
            onBlur={() => setFocusedField(null)}
            placeholder="Company (optional)"
            className="w-full px-3 py-1.5 rounded-lg outline-none transition-all text-[13px]"
            style={{
              backgroundColor: 'transparent',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              height: '36px',
            }}
          />
        </div>

        <div>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            onFocus={() => setFocusedField('message')}
            onBlur={() => {
              setFocusedField(null);
              handleBlur('message');
            }}
            placeholder="Project details... (include phone/WhatsApp for direct contact)"
            rows="2"
            className="w-full px-3 py-1.5 rounded-lg outline-none transition-all resize-none text-[13px]"
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${touchedFields.message && (!formData.message || formData.message.length < 20) ? '#ef4444' : 'var(--border)'}`,
              color: 'var(--text)',
              minHeight: '56px',
            }}
          />
          <div className="h-3 mt-0.5">
            {touchedFields.message && !formData.message && (
              <div className="text-[9px] text-red-500">
                Please tell us about your project
              </div>
            )}
            {touchedFields.message &&
              formData.message &&
              formData.message.length < 20 && (
                <div className="text-[9px] text-yellow-500">
                  {20 - formData.message.length} more characters needed
                </div>
              )}
          </div>
        </div>

        {errorMessage && (
          <div
            className="flex items-center gap-1.5 p-1.5 rounded-lg animate-fade-in"
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
            }}
          >
            <FiAlertCircle className="w-3 h-3 text-red-500 flex-shrink-0" />
            <span className="text-[9px] text-red-500">{errorMessage}</span>
          </div>
        )}

        {submitStatus === 'success' && (
          <div
            className="p-2.5 rounded-lg animate-fade-in"
            style={{
              backgroundColor: 'rgba(34, 197, 94, 0.06)',
              border: '1px solid rgba(34, 197, 94, 0.2)',
            }}
          >
            <div className="flex items-start gap-2">
              <FiCheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[11px] font-medium text-green-500">
                  Inquiry submitted!
                </p>
                <p className="text-[10px]" style={{ color: 'var(--muted)' }}>
                  We'll respond within 24 hours.
                </p>
                <div className="flex items-center gap-1 mt-1 text-[9px] text-yellow-500">
                  <FiAlertTriangle className="w-2.5 h-2.5" />
                  <span>Check spam folder if you don't see our reply!</span>
                </div>
                {formData.phone && (
                  <p className="text-[9px] text-green-500 mt-0.5">
                    ✅ Phone provided for backup contact.
                  </p>
                )}
                {!formData.phone && (
                  <p className="text-[9px] text-yellow-500 mt-0.5">
                    ⚠️ No phone provided. Check spam or contact us directly.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || !isFormValid}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg font-medium transition-all hover:opacity-80 disabled:opacity-50 text-[13px]"
          style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
        >
          {isSubmitting ? (
            <>
              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              Send Inquiry
              <FiSend className="w-3.5 h-3.5" />
            </>
          )}
        </button>

        <div
          className="flex items-center justify-center gap-2.5 text-[9px]"
          style={{ color: 'var(--muted)' }}
        >
          <span className="flex items-center gap-1">
            <span
              className={`w-1.5 h-1.5 rounded-full ${formData.name.trim() ? 'bg-green-500' : 'bg-gray-400'}`}
            />
            Name
          </span>
          <span className="flex items-center gap-1">
            <span
              className={`w-1.5 h-1.5 rounded-full ${emailValidation.isValid ? 'bg-green-500' : 'bg-gray-400'}`}
            />
            Email
          </span>
          <span className="flex items-center gap-1">
            <span
              className={`w-1.5 h-1.5 rounded-full ${formData.message.trim().length >= 20 ? 'bg-green-500' : 'bg-gray-400'}`}
            />
            Message
          </span>
        </div>

        <div
          className="flex items-center justify-between text-[9px]"
          style={{ color: 'var(--muted)' }}
        >
          <span className="flex items-center gap-1">
            <FiCheck className="w-2.5 h-2.5 text-green-500" />
            Privacy secure
          </span>
          <WorkflowTooltip />
        </div>
      </form>
    </div>
  );
};

// =============================================
// MAIN COMPONENT - ServiceDetail
// =============================================
function ServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();

  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inquirySuccess, setInquirySuccess] = useState(false);

  const [relatedServices, setRelatedServices] = useState([]);
  const [popularServices, setPopularServices] = useState([]);

  // ✅ REMOVED: metaData - No client-side OG generation
  // Social media scrapers get OG tags from server API

  // Load service
  useEffect(() => {
    const loadService = async () => {
      if (!id) {
        setError('No service ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const q = query(
          collection(db, 'readyMadeServices'),
          where('status', '==', 'active'),
          orderBy('popular', 'desc'),
          orderBy('order', 'asc'),
        );
        const snapshot = await getDocs(q);
        const servicesList = [];

        snapshot.forEach(doc => {
          const data = doc.data();
          servicesList.push({
            id: doc.id,
            ...data,
          });
        });

        const foundService = servicesList.find(s => s.id === id);

        if (!foundService) {
          setError('Service not found');
          setLoading(false);
          return;
        }

        setService(foundService);

        const related = servicesList
          .filter(s => s.id !== id && s.category === foundService.category)
          .slice(0, 6);
        setRelatedServices(related);

        const popular = servicesList
          .filter(s => s.id !== id && s.popular)
          .slice(0, 4);
        setPopularServices(popular);
      } catch (err) {
        console.error('Error loading service:', err);
        setError(err.message || 'Failed to load service');
      } finally {
        setLoading(false);
      }
    };

    loadService();
  }, [id]);

  const handleInquirySuccess = () => {
    setInquirySuccess(true);
    setTimeout(() => setInquirySuccess(false), 5000);
  };

  const formatDate = dateString => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Render suggestion section
  const renderSuggestionSection = (
    title,
    icon,
    items,
    viewAllLink = '/services',
  ) => {
    if (!items || items.length === 0) return null;

    return (
      <motion.div variants={fadeInUp} className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {icon}
            <h3
              className="text-sm font-semibold"
              style={{ color: 'var(--text)' }}
            >
              {title}
            </h3>
          </div>
          <Link
            to={viewAllLink}
            className="text-xs hover:underline flex items-center gap-1"
            style={{ color: 'var(--muted)' }}
          >
            View All <FiArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {items.slice(0, 6).map(item => (
            <div
              key={item.id}
              onClick={() => navigate(`/service/${item.id}`)}
              className="group cursor-pointer rounded-lg overflow-hidden border transition-all hover:shadow-md hover:-translate-y-0.5"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="aspect-video overflow-hidden bg-gray-100 dark:bg-gray-800">
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt={item.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FiPackage
                      className="w-8 h-8"
                      style={{ color: 'var(--muted)' }}
                    />
                  </div>
                )}
              </div>
              <div className="p-2">
                <p
                  className="text-xs font-medium truncate"
                  style={{ color: 'var(--text)' }}
                >
                  {item.name}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span
                    className="text-[10px] capitalize"
                    style={{ color: 'var(--muted)' }}
                  >
                    {item.category}
                  </span>
                  <span
                    className="text-[10px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    ${item.price}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    );
  };

  if (loading) return <ServiceDetailSkeleton />;

  if (error || !service) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--bg)' }}
      >
        <div className="text-center p-8">
          <h2
            className="text-2xl font-bold mb-4"
            style={{ color: 'var(--text)' }}
          >
            {error || 'Service not found'}
          </h2>
          <p className="text-sm mb-6" style={{ color: 'var(--muted)' }}>
            The service you're looking for doesn't exist or has been removed.
          </p>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm transition-all hover:gap-3"
            style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
          >
            <FiArrowLeft className="w-4 h-4" />
            Back to Services
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = `${window.location.origin}/service/${service.id}`;
  const pageTitle = service.name;
  const pageDescription = service.description;

  const stats = [
    {
      icon: FiDollarSign,
      label: 'Starting Price',
      value: `$${service.price}`,
      color: 'text-green-500',
    },
    {
      icon: FiClock,
      label: 'Duration',
      value: service.duration || '2-4 weeks',
      color: 'text-blue-500',
    },
    {
      icon: FiCheckCircle,
      label: 'Features',
      value: service.features?.length || 0,
      color: 'text-purple-500',
    },
    {
      icon: FiUsers,
      label: 'For',
      value: service.targetAudience?.[0] || 'Everyone',
      color: 'text-orange-500',
    },
  ];

  // Schema markup
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    description: service.description,
    provider: { '@type': 'Person', name: 'WD Hajbul' },
    offers: { '@type': 'Offer', price: service.price, priceCurrency: 'USD' },
  };

  return (
    <>
      {/*
        ✅ HELMET - Only basic page info
        ❌ NO OG TAGS - Server handles OG generation via API
        Social media scrapers get OG from /api/ready-made-services?action=social-preview&id=xxx
      */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={service.tags?.join(', ') || ''} />
        <link rel="canonical" href={shareUrl} />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <div
        className="min-h-screen"
        style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
      >
        {/* Sticky Navigation */}
        <div
          className="sticky top-[85px] z-30 backdrop-blur-md border-b"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link
              to="/services"
              className="flex items-center gap-2 text-sm transition-all hover:gap-3"
              style={{ color: 'var(--muted)' }}
            >
              <FiArrowLeft className="w-4 h-4" />
              Back to Services
            </Link>
            <div className="flex items-center gap-3">
              <ShareButtons
                url={shareUrl}
                title={service.name}
                description={service.description}
                imageUrl={service.imageUrl || ''}
                service={service}
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 pt-24 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Column */}
            <div className="lg:col-span-8 space-y-6">
              {/* Image */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                className="relative w-full rounded-2xl overflow-hidden bg-black/5 dark:bg-white/5"
              >
                <div className="relative w-full pt-[56.25%]">
                  {service.imageUrl ? (
                    <>
                      <img
                        src={service.imageUrl}
                        alt={service.name}
                        className="absolute top-0 left-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                    </>
                  ) : (
                    <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                      <FiPackage
                        className="w-16 h-16"
                        style={{ color: 'var(--muted)' }}
                      />
                    </div>
                  )}
                  <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                    {service.popular && (
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-500 text-white flex items-center gap-1 shadow-lg">
                        <FiStar className="w-3 h-3" /> Most Popular
                      </span>
                    )}
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-black/60 text-white backdrop-blur-sm capitalize">
                      {service.category || 'Service'}
                    </span>
                  </div>
                </div>
              </motion.div>

              {/* Title & Meta */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
              >
                <h1
                  className="text-2xl md:text-3xl font-bold mb-2"
                  style={{ color: 'var(--text)' }}
                >
                  {service.name}
                </h1>
                <div
                  className="flex flex-wrap gap-3 text-xs"
                  style={{ color: 'var(--muted)' }}
                >
                  <span className="flex items-center gap-1.5">
                    <FiFolder className="w-3.5 h-3.5" />
                    <span className="capitalize">{service.category}</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <FiDollarSign className="w-3.5 h-3.5" />
                    Starting from ${service.price}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <FiClock className="w-3.5 h-3.5" />
                    {service.duration || '2-4 weeks'}
                  </span>
                  {service.createdAt && (
                    <span className="flex items-center gap-1.5">
                      <FiCalendar className="w-3.5 h-3.5" />
                      {formatDate(service.createdAt)}
                    </span>
                  )}
                </div>
              </motion.div>

              {/* Stats */}
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-2 sm:grid-cols-4 gap-3"
              >
                {stats.map((stat, idx) => {
                  const Icon = stat.icon;
                  return (
                    <motion.div
                      key={idx}
                      variants={fadeInUp}
                      className="p-3 rounded-xl border text-center"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      <Icon className={`w-4 h-4 mx-auto mb-1 ${stat.color}`} />
                      <p
                        className="text-sm font-bold"
                        style={{ color: 'var(--text)' }}
                      >
                        {stat.value}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--muted)' }}>
                        {stat.label}
                      </p>
                    </motion.div>
                  );
                })}
              </motion.div>

              {/* Description */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-1 h-6 rounded-full bg-blue-500" />
                  <h2
                    className="text-lg font-semibold"
                    style={{ color: 'var(--text)' }}
                  >
                    About This Service
                  </h2>
                </div>
                <p
                  className="leading-relaxed whitespace-pre-wrap"
                  style={{ color: 'var(--muted)' }}
                >
                  {service.description}
                </p>
              </motion.div>

              {/* Features */}
              {service.features && service.features.length > 0 && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeInUp}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-1 h-6 rounded-full bg-green-500" />
                    <h2
                      className="text-lg font-semibold"
                      style={{ color: 'var(--text)' }}
                    >
                      What's Included
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <FiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span style={{ color: 'var(--text)' }}>{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Deliverables */}
              {service.deliverables && service.deliverables.length > 0 && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeInUp}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-1 h-6 rounded-full bg-purple-500" />
                    <h2
                      className="text-lg font-semibold"
                      style={{ color: 'var(--text)' }}
                    >
                      Deliverables
                    </h2>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {service.deliverables.map((item, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 rounded-full text-sm"
                        style={{
                          backgroundColor: 'var(--border)',
                          color: 'var(--text)',
                        }}
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Tags */}
              {service.tags && service.tags.length > 0 && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeInUp}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-1 h-6 rounded-full bg-orange-500" />
                    <h2
                      className="text-lg font-semibold"
                      style={{ color: 'var(--text)' }}
                    >
                      Tags
                    </h2>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {service.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 rounded-full text-sm"
                        style={{
                          backgroundColor: 'var(--border)',
                          color: 'var(--muted)',
                        }}
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Target Audience */}
              {service.targetAudience && service.targetAudience.length > 0 && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeInUp}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-1 h-6 rounded-full bg-cyan-500" />
                    <h2
                      className="text-lg font-semibold"
                      style={{ color: 'var(--text)' }}
                    >
                      Ideal For
                    </h2>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {service.targetAudience.map((audience, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 rounded-full text-sm"
                        style={{
                          backgroundColor: 'var(--border)',
                          color: 'var(--text)',
                        }}
                      >
                        <FiUsers className="w-3 h-3 inline mr-1" />
                        {audience}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Revisions & Support */}
              {(service.revisions || service.support) && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeInUp}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                >
                  {service.revisions && (
                    <div
                      className="p-4 rounded-xl"
                      style={{ backgroundColor: 'var(--border-light)' }}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <FiInfo
                          className="w-4 h-4"
                          style={{ color: 'var(--muted)' }}
                        />
                        <span
                          className="text-sm font-medium"
                          style={{ color: 'var(--text)' }}
                        >
                          Revisions
                        </span>
                      </div>
                      <p className="text-sm" style={{ color: 'var(--muted)' }}>
                        {service.revisions}
                      </p>
                    </div>
                  )}
                  {service.support && (
                    <div
                      className="p-4 rounded-xl"
                      style={{ backgroundColor: 'var(--border-light)' }}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <FiMessageCircle
                          className="w-4 h-4"
                          style={{ color: 'var(--muted)' }}
                        />
                        <span
                          className="text-sm font-medium"
                          style={{ color: 'var(--text)' }}
                        >
                          Support
                        </span>
                      </div>
                      <p className="text-sm" style={{ color: 'var(--muted)' }}>
                        {service.support}
                      </p>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Inline Ad */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                className="p-3 rounded-xl border border-dashed text-center"
                style={{ borderColor: 'var(--border)' }}
              >
                <div
                  className="flex items-center justify-center gap-2 text-[10px]"
                  style={{ color: 'var(--muted)' }}
                >
                  <span>📢</span>
                  <span>Advertisement</span>
                </div>
                <div
                  className="min-h-[60px] flex items-center justify-center"
                  style={{ backgroundColor: 'var(--border-light)' }}
                >
                  <span className="text-xs" style={{ color: 'var(--muted)' }}>
                    Google Ad Space
                  </span>
                </div>
              </motion.div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="lg:col-span-4 space-y-6">
              <FiverrCard variant="sidebar" />

              {/* Inquiry Form */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInRight}
                className="p-4 rounded-xl border"
                style={{ borderColor: 'var(--border)' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div
                    className="p-1.5 rounded-lg"
                    style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}
                  >
                    <FiMessageCircle className="w-4 h-4 text-blue-500" />
                  </div>
                  <h3
                    className="text-sm font-semibold"
                    style={{ color: 'var(--text)' }}
                  >
                    Send Inquiry
                  </h3>
                </div>
                <InquiryForm
                  service={service}
                  onSuccess={handleInquirySuccess}
                />
              </motion.div>

              {/* Google Ad */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInRight}
                className="p-3 rounded-xl border border-dashed text-center"
                style={{ borderColor: 'var(--border)' }}
              >
                <div
                  className="flex items-center justify-center gap-2 text-[10px]"
                  style={{ color: 'var(--muted)' }}
                >
                  <span>📢</span>
                  <span>Advertisement</span>
                </div>
                <div
                  className="min-h-[250px] flex items-center justify-center"
                  style={{ backgroundColor: 'var(--border-light)' }}
                >
                  <span className="text-xs" style={{ color: 'var(--muted)' }}>
                    Google Ad Space
                  </span>
                </div>
              </motion.div>

              {/* Service Details */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInRight}
                className="p-4 rounded-xl border"
                style={{ borderColor: 'var(--border)' }}
              >
                <h3
                  className="text-sm font-semibold mb-3"
                  style={{ color: 'var(--text)' }}
                >
                  <FiFolder className="inline mr-2" /> Service Details
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--muted)' }}>Category</span>
                    <span
                      className="capitalize"
                      style={{ color: 'var(--text)' }}
                    >
                      {service.category}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--muted)' }}>Price</span>
                    <span style={{ color: 'var(--text)' }}>
                      ${service.price}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--muted)' }}>Duration</span>
                    <span style={{ color: 'var(--text)' }}>
                      {service.duration || '2-4 weeks'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--muted)' }}>Features</span>
                    <span style={{ color: 'var(--text)' }}>
                      {service.features?.length || 0}
                    </span>
                  </div>
                  {service.popular && (
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--muted)' }}>Status</span>
                      <span className="text-yellow-500 flex items-center gap-1">
                        <FiStar className="w-3 h-3" /> Most Popular
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Contact */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInRight}
                className="p-4 rounded-xl border text-center"
                style={{
                  borderColor: 'var(--border)',
                  backgroundColor: 'var(--border-light)',
                }}
              >
                <div className="flex items-center justify-center mb-2">
                  <div
                    className="p-2 rounded-full"
                    style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}
                  >
                    <FiMail className="w-5 h-5 text-blue-500" />
                  </div>
                </div>
                <h3
                  className="text-sm font-semibold mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  Have any questions?
                </h3>
                <p className="text-xs mb-3" style={{ color: 'var(--muted)' }}>
                  We're here to help. Reach out and we'll get back to you within
                  24 hours.
                </p>
                <div className="flex flex-col gap-2">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all hover:gap-3"
                    style={{
                      backgroundColor: 'var(--text)',
                      color: 'var(--bg)',
                    }}
                  >
                    <FiMail className="w-3.5 h-3.5" />
                    Contact Us
                    <FiArrowRight className="w-3 h-3" />
                  </Link>
                  <div
                    className="flex items-center justify-center gap-4 text-[10px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    <a
                      href="tel:+8801884521448"
                      className="flex items-center gap-1 cursor-pointer"
                    >
                      <FiPhone className="w-3 h-3" />
                      +8801884521448
                    </a>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <FiClock className="w-3 h-3" />
                      Worldwide available remotely
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Suggestions */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="mt-8 pt-8 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-8 rounded-full bg-gradient-to-b from-blue-500 to-purple-500" />
              <h2
                className="text-xl font-bold"
                style={{ color: 'var(--text)' }}
              >
                Explore More Services
              </h2>
              <span
                className="text-xs px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--muted)',
                }}
              >
                Discover
              </span>
            </div>

            {renderSuggestionSection(
              'Related Services',
              <FiTrendingUp className="w-4 h-4 text-blue-500" />,
              relatedServices,
            )}
            {renderSuggestionSection(
              'Popular Services',
              <FiAward className="w-4 h-4 text-yellow-500" />,
              popularServices,
            )}

            <motion.div
              variants={fadeInUp}
              className="p-4 rounded-xl border border-dashed text-center mt-6"
              style={{ borderColor: 'var(--border)' }}
            >
              <div
                className="flex items-center justify-center gap-2 text-[10px]"
                style={{ color: 'var(--muted)' }}
              >
                <span>📢</span>
                <span>Advertisement</span>
              </div>
              <div
                className="min-h-[90px] flex items-center justify-center"
                style={{ backgroundColor: 'var(--border-light)' }}
              >
                <span className="text-xs" style={{ color: 'var(--muted)' }}>
                  Google Ad Space
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <style jsx>{`
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out forwards;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
}

export default ServiceDetail;
